/**
 * Validates a company name.
 * Returns an error message if the name is invalid, otherwise returns undefined.
 * @param name - The company name to validate.
 * @returns error message string if the name is invalid, otherwise returns undefined.
 */
export const validateCompanyName = (name: string | undefined) => {
	if (!name) {
		return 'Company name is required';
	}
	if (name.length < 3) {
		return 'Company name needs to be at least 3 characters long';
	}

	return undefined;
};

/**
 * Validates a country select option.
 * Returns an error message if the country is invalid, otherwise returns undefined.
 * @param country - The country select option to validate.
 * @returns error message string if the country is invalid, otherwise undefined.
 */
export const validateCountryCode = (
	twoLetterIsoCode: string | null,
): string | undefined => {
	if (!twoLetterIsoCode || twoLetterIsoCode.length !== 2) {
		return 'Country is required';
	}

	return undefined;
};

/**
 * Validates a Younium ID.
 * Returns an error message if the ID is invalid, otherwise returns undefined.
 * @param youniumId - The Younium ID to validate.
 * @returns error message string if the ID is invalid, otherwise undefined.
 */
export const validateYouniumId = (youniumId: string | undefined) => {
	const guidPattern =
		/^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/i;

	if (youniumId && !guidPattern.test(youniumId)) {
		return 'Younium ID must be a valid GUID (e.g. 1cd2fa57-8677-4813-b663-decb0eba07f3)';
	}

	return undefined;
};

/**
 * Validates a Hubspot ID.
 * Returns an error message if the ID is invalid, otherwise returns undefined.
 * @param hubspotId - The Younium ID to validate.
 * @returns error message string if the ID is invalid, otherwise undefined.
 */
export const validateHubspotId = (hubspotId: string | undefined) => {
	if (hubspotId && !isParseableToNumber(hubspotId)) {
		return 'Hubspot ID must be a valid number (e.g. 235346)';
	}

	return undefined;
};

/**
 * Validates a domain name.
 * Returns an error message if the domain is invalid, otherwise returns undefined.
 * @param domain - The domain name to validate.
 * @returns error message string if the domain is invalid, otherwise undefined.
 */
export const validateDomain = (domain: string | undefined) => {
	const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;
	if (domain && !domainRegex.test(domain)) {
		return 'Domain must be a valid domain name (e.g. example.com)';
	}

	return undefined;
};

const isParseableToNumber = (value: unknown): boolean => !isNaN(Number(value));
